import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LogoUploader from "@/components/LogoUploader";
import { UserCircle2, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CustomerAvatarCard
 *
 * Lets a signed-in customer edit their display name AND set/clear a profile
 * picture. Names are stored server-side and re-normalised for search, but
 * publicly rendered as "First-initial. LastName" via `public_customer()`.
 */
export default function CustomerAvatarCard() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [avatar, setAvatar] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarDirty, setAvatarDirty] = useState(false);
  const initialName = user?.name || "";
  const nameDirty = name.trim() !== initialName.trim();

  useEffect(() => {
    setName(user?.name || "");
    setAvatar(user?.avatar_data_url || "");
    setAvatarDirty(false);
  }, [user?.name, user?.avatar_data_url]);

  const save = async () => {
    setSaving(true);
    try {
      const jobs = [];
      if (nameDirty) {
        jobs.push(api.put("/me/customer/prefs", { name: name.trim() }));
      }
      if (avatarDirty) {
        jobs.push(api.put("/me/customer/avatar", { avatar_data_url: avatar || "" }));
      }
      if (!jobs.length) return;
      await Promise.all(jobs);
      const parts = [];
      if (nameDirty) parts.push("name");
      if (avatarDirty) parts.push(avatar ? "picture" : "picture removal");
      toast.success(`Saved ${parts.join(" & ")}`);
      setAvatarDirty(false);
      if (refresh) refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    setAvatar("");
    setAvatarDirty(true);
  };

  const dirty = nameDirty || avatarDirty;

  return (
    <Card className="rounded-md border border-border p-6" data-testid="customer-avatar-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-[#B31E1E]/5 text-[#B31E1E] flex items-center justify-center shrink-0">
          <UserCircle2 size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl">Profile identity</h3>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Update your display name and optional avatar. Publicly, businesses
            still see the privacy-preserving format
            <span className="font-mono"> &nbsp;First-initial. LastName</span>.
            Picture is auto-resized to 300×300 before upload.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="customer-name"
            className="text-xs font-mono uppercase tracking-wider text-gray-600"
          >
            Full name
          </Label>
          <Input
            id="customer-name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            className="rounded-sm mt-1"
            placeholder="First LastName"
            data-testid="customer-name-input"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Shown to businesses as{" "}
            <span className="font-mono">
              {(() => {
                const parts = name.trim().split(/\s+/);
                if (parts.length < 2) return name.trim() || "—";
                return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
              })()}
            </span>
          </p>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">
            Profile picture
          </Label>
          <div className="mt-1">
            <LogoUploader
              value={avatar}
              onChange={(v) => {
                setAvatar(v || "");
                setAvatarDirty(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving || !name.trim()}
          className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
          data-testid="customer-avatar-save"
        >
          {saving ? (
            <>
              <Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save size={14} className="mr-1.5" /> Save changes
            </>
          )}
        </Button>
        {avatar && (
          <Button
            type="button"
            variant="outline"
            onClick={remove}
            disabled={saving}
            className="rounded-[35px]"
            data-testid="customer-avatar-remove"
          >
            <Trash2 size={13} className="mr-1.5" /> Remove picture
          </Button>
        )}
        {!dirty && !saving && (
          <span className="text-xs font-mono text-gray-400">All changes saved</span>
        )}
      </div>
    </Card>
  );
}
